import { useRecordContext, useTranslate } from 'react-admin';
import { Chip, Stack, Typography } from '@mui/material';
import type { AdminSubscription } from '@rekindle/shared/types/adminSubscription';

export function useSubscriptionText() {
  const translate = useTranslate();
  return (key: string, fallback: string, values: Record<string, unknown> = {}) =>
    translate(`rekindle.subscription.${key}`, { _: fallback, ...values });
}

export function SubscriptionSource({ value }: { value?: AdminSubscription }) {
  const t = useSubscriptionText();
  if (!value) return <Typography variant="body2">{t('unknown', 'Unavailable')}</Typography>;
  const label = value.source === 'revenuecat' ? 'RevenueCat'
    : value.source === 'manual' ? t('offered', 'Offered access') : t('free', 'Free');
  const store = value.store === 'play_store' ? 'Google Play' : value.store === 'app_store' ? 'App Store' : null;
  return <Stack spacing={0.5} alignItems="flex-start">
    <Chip size="small" variant="outlined" color={value.source === 'revenuecat' ? 'primary' : 'default'} label={label} />
    {store && <Typography variant="caption" color="text.secondary">{store}</Typography>}
  </Stack>;
}

export function SubscriptionAccess({ value }: { value?: AdminSubscription }) {
  const t = useSubscriptionText();
  if (!value) return <Typography variant="body2">{t('unknown', 'Unavailable')}</Typography>;
  const date = value.accessUntil ? new Date(value.accessUntil).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null;
  const text = value.status === 'expired'
    ? date ? t('expiredAt', 'Ended %{date}', { date }) : t('expired', 'Expired')
    : value.isLifetime ? t('lifetime', 'Lifetime')
    : value.status === 'free' ? t('noPaidAccess', 'No paid access')
    : date ? t('accessUntil', 'Access until %{date}', { date }) : t('unknown', 'Unavailable');
  return <Typography variant="body2" color={value.status === 'expired' ? 'text.secondary' : 'text.primary'}>{text}</Typography>;
}

export function SubscriptionSourceField(_props: { label?: string; sortable?: boolean }) {
  return <SubscriptionSource value={useRecordContext()?.subscription} />;
}

export function SubscriptionAccessField(_props: { label?: string; sortable?: boolean }) {
  return <SubscriptionAccess value={useRecordContext()?.subscription} />;
}
